'use client'

import { cn } from '@/lib/utils'
import type { FilterSettings } from './filterPresets'
import { DEFAULT_FILTERS } from './filterPresets'

interface FilterControlsProps {
  /** Current filter settings */
  filters: FilterSettings
  /** Called when filters change */
  onChange: (filters: FilterSettings) => void
  /** Called when preset should be cleared (manual adjustment) */
  onPresetClear: () => void
}

interface SliderConfig {
  key: keyof FilterSettings
  label: string
  min: number
  max: number
  step: number
  unit: string
}

const SLIDERS: SliderConfig[] = [
  {
    key: 'brightness',
    label: 'Brightness',
    min: 50,
    max: 150,
    step: 1,
    unit: '%',
  },
  { key: 'contrast', label: 'Contrast', min: 50, max: 150, step: 1, unit: '%' },
  {
    key: 'saturation',
    label: 'Saturation',
    min: 0,
    max: 200,
    step: 1,
    unit: '%',
  },
  { key: 'hueRotate', label: 'Hue', min: 0, max: 360, step: 1, unit: '°' },
  { key: 'sepia', label: 'Sepia', min: 0, max: 100, step: 1, unit: '%' },
  {
    key: 'grayscale',
    label: 'Grayscale',
    min: 0,
    max: 100,
    step: 1,
    unit: '%',
  },
]

/**
 * FilterControls
 *
 * Sliders for adjusting individual filter values
 */
export function FilterControls({
  filters,
  onChange,
  onPresetClear,
}: FilterControlsProps) {
  const handleChange = (key: keyof FilterSettings, value: number) => {
    onChange({ ...filters, [key]: value })
    onPresetClear() // Clear preset when manually adjusting
  }

  const handleReset = (key: keyof FilterSettings) => {
    onChange({ ...filters, [key]: DEFAULT_FILTERS[key] })
  }

  const isModified = (key: keyof FilterSettings) => {
    return filters[key] !== DEFAULT_FILTERS[key]
  }

  return (
    <div className="space-y-4">
      {SLIDERS.map((slider) => {
        const value = filters[slider.key]
        const defaultValue = DEFAULT_FILTERS[slider.key]
        const modified = isModified(slider.key)

        // Calculate percentage position for the track fill
        const range = slider.max - slider.min
        const percentage = ((value - slider.min) / range) * 100

        return (
          <div key={slider.key} className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">
                {slider.label}
              </label>
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'text-xs tabular-nums min-w-[3rem] text-right',
                    modified ? 'text-foreground' : 'text-muted-foreground'
                  )}
                >
                  {value}
                  {slider.unit}
                </span>
                {modified && (
                  <button
                    onClick={() => handleReset(slider.key)}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    title="Reset to default"
                  >
                    ↺
                  </button>
                )}
              </div>
            </div>

            <div className="relative">
              <input
                type="range"
                min={slider.min}
                max={slider.max}
                step={slider.step}
                value={value}
                onChange={(e) =>
                  handleChange(slider.key, Number(e.target.value))
                }
                className={cn(
                  'w-full h-2 rounded-lg appearance-none cursor-pointer',
                  'bg-muted',
                  '[&::-webkit-slider-thumb]:appearance-none',
                  '[&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4',
                  '[&::-webkit-slider-thumb]:rounded-full',
                  '[&::-webkit-slider-thumb]:bg-primary',
                  '[&::-webkit-slider-thumb]:shadow-sm',
                  '[&::-webkit-slider-thumb]:cursor-pointer',
                  '[&::-webkit-slider-thumb]:transition-transform',
                  '[&::-webkit-slider-thumb]:hover:scale-110',
                  '[&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4',
                  '[&::-moz-range-thumb]:rounded-full',
                  '[&::-moz-range-thumb]:bg-primary',
                  '[&::-moz-range-thumb]:border-0',
                  '[&::-moz-range-thumb]:cursor-pointer'
                )}
                style={{
                  background: `linear-gradient(to right, hsl(var(--primary)) 0%, hsl(var(--primary)) ${percentage}%, hsl(var(--muted)) ${percentage}%, hsl(var(--muted)) 100%)`,
                }}
              />

              {/* Default value marker */}
              {slider.min < defaultValue && defaultValue < slider.max && (
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-0.5 h-3 bg-muted-foreground/30 pointer-events-none"
                  style={{
                    left: `${((defaultValue - slider.min) / range) * 100}%`,
                  }}
                />
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
